import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #gridMutationObserver;

  /** @type {HTMLElement | undefined} */
  #observedGrid;

  #gridImageSizeSyncQueued = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#syncCurrentGridImageSizes();
    this.#observeGridChildren();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopObservingGridChildren();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeGridChildren();
    this.#syncCurrentGridImageSizes();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#syncMobileImageSizes(grid, value);
  }

  #syncCurrentGridImageSizes() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;

    const gridView = grid.getAttribute('product-grid-view');
    if (gridView) this.#syncMobileImageSizes(grid, gridView);
  }

  #observeGridChildren() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) {
      this.#stopObservingGridChildren();
      return;
    }
    if (this.#observedGrid === grid) return;

    this.#stopObservingGridChildren();
    this.#observedGrid = grid;
    this.#gridMutationObserver = new MutationObserver(this.#queueGridImageSizeSync);
    this.#gridMutationObserver.observe(grid, { childList: true });
  }

  #stopObservingGridChildren() {
    this.#gridMutationObserver?.disconnect();
    this.#gridMutationObserver = undefined;
    this.#observedGrid = undefined;
    this.#gridImageSizeSyncQueued = false;
  }

  #queueGridImageSizeSync = () => {
    if (this.#gridImageSizeSyncQueued) return;

    this.#gridImageSizeSyncQueued = true;
    queueMicrotask(() => {
      this.#gridImageSizeSyncQueued = false;
      if (this.isConnected) this.#syncCurrentGridImageSizes();
    });
  };

  /**
   * Keeps the mobile entry of each card image's `sizes` attribute honest when the shopper switches
   * between the multi-column and full-width mobile grid views.
   *
   * Liquid renders `sizes` for the section's default mobile column count, but the view is a runtime,
   * session-scoped choice. Without this, a shopper on a browser that does not support `sizes="auto"`
   * would keep a half-width candidate in the full-width view. Browsers only ever upgrade to a larger
   * candidate on a `sizes` change, so widening never triggers a redundant download of a smaller one.
   *
   * @param {Element} grid
   * @param {string} value
   */
  #syncMobileImageSizes(grid, value) {
    // The mobile entry is the last, condition-less entry of the sizes list; every preceding entry is
    // guarded by a `min-width` of at least 750px, so rewriting it cannot affect desktop rendering.
    const fullWidthViews = ['mobile-single', 'mobile-editorial'];
    if (!fullWidthViews.includes(value) && value !== 'default') return;

    const images = [
      ...Array.from(grid.querySelectorAll('img[sizes]')).map((image) => ({
        image,
        card: image.closest('.product-grid__item'),
      })),
      ...Array.from(grid.querySelectorAll('template[data-deferred-media]')).flatMap((template) => {
        if (!(template instanceof HTMLTemplateElement)) return [];

        return Array.from(template.content.querySelectorAll('img[sizes]')).map((image) => ({
          image,
          card: template.closest('.product-grid__item'),
        }));
      }),
    ];

    for (const { image, card } of images) {
      if (!(image instanceof HTMLImageElement)) continue;

      if (image.dataset.baseSizes == null) image.dataset.baseSizes = image.sizes;

      const baseSizes = image.dataset.baseSizes;
      const separator = baseSizes.lastIndexOf(',');
      if (separator === -1) continue;

      // In the editorial view only every fifth card spans both columns.
      const spansFullWidth =
        value === 'mobile-single' ||
        (value === 'mobile-editorial' && card?.matches(':nth-child(5n + 5)'));

      if (spansFullWidth) {
        image.sizes = `${baseSizes.slice(0, separator)}, 100vw`;
      } else if (value === 'mobile-editorial') {
        image.sizes = `${baseSizes.slice(0, separator)}, 50vw`;
      } else {
        image.sizes = baseSizes;
      }
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
